import { groupBy } from 'lodash';

import { RootState } from '../../../init-store';
import { selectFullHistoryArtifactsEnabled } from '../../shared/features/flags/selectors';
import { ARTIFACTS_FILTER_OPTION } from './constants';
import { roundDate } from './helpers/round-date';
import { FETCHING_STATUS } from './types';

export const selectHistoryGroupedByDay = (state: RootState) =>
    Object.entries(
        groupBy(state.conferencesHistory.list.items, (item) => {
            const startedAtDate = new Date(item.startedAt);
            roundDate(startedAtDate);
            startedAtDate.setDate(1);
            return Number(startedAtDate);
        })
    )
        .sort(([a], [b]) => Number(b) - Number(a))
        .map(([, monthConferences]) =>
            Object.entries(groupBy(monthConferences, (item) => new Date(item.startedAt).getDate()))
                .sort(([a], [b]) => Number(b) - Number(a))
                .map(([, dayConferences]) => dayConferences)
        );

export const selectIsNextBatchLoading = (state: RootState) =>
    state.conferencesHistory.fetching.status === FETCHING_STATUS.IN_PROGRESS &&
    state.conferencesHistory.list.items.length > 0;

export const selectIsFullLoading = (state: RootState) =>
    state.conferencesHistory.fetching.status === FETCHING_STATUS.IN_PROGRESS &&
    state.conferencesHistory.list.items.length === 0;

export const selectIsEmptyResult = (state: RootState) =>
    state.conferencesHistory.list.items &&
    state.conferencesHistory.list.items.length === 0 &&
    state.conferencesHistory.fetching.status !== FETCHING_STATUS.IN_PROGRESS &&
    !state.conferencesHistory.list.iterationKey;

export const selectIsFilterReset = ({ conferencesHistory: { filter } }: RootState) =>
    !filter.from && !filter.to && filter.artifacts === ARTIFACTS_FILTER_OPTION.ALL_CONFS;

export const selectFilterArtifacts = (state: RootState) => {
    const storeValue = state.conferencesHistory.filter.artifacts;
    const historyFullArtifacts = selectFullHistoryArtifactsEnabled(state);
    if (
        !historyFullArtifacts &&
        [ARTIFACTS_FILTER_OPTION.WITH_RECORDING, ARTIFACTS_FILTER_OPTION.WITH_SUMMARY].includes(storeValue)
    ) {
        return ARTIFACTS_FILTER_OPTION.ALL_CONFS;
    }
    return storeValue;
};

export const selectDateRangeType = ({ conferencesHistory: { filter } }: RootState) => filter.dateRangeType;

export const selectFilterSelectedMonth = ({ conferencesHistory: { filter } }: RootState) => filter.selectedMonth;

export const selectFilterFromTo = ({ conferencesHistory: { filter } }: RootState) => ({
    from: filter.from,
    to: filter.to,
});

export const selectIterationKey = ({ conferencesHistory: { list } }: RootState) => list.iterationKey;

export const selectHasMoreConferences = ({ conferencesHistory: { list } }: RootState) => !!list.iterationKey;

export const selectIsFullFetchingFailed = ({ conferencesHistory: { fetching, list } }: RootState) =>
    fetching.status === FETCHING_STATUS.ERROR && list.items.length === 0;

export const selectIsBatchFetchingFailed = ({ conferencesHistory: { fetching, list } }: RootState) =>
    fetching.status === FETCHING_STATUS.ERROR && list.items.length > 0;

export const selectSelectedSessionId = ({ conferencesHistory: { sidebar } }: RootState) => sidebar.selectedSessionId;

export const selectSession =
    (sessionId: string) =>
    ({ conferencesHistory: { list } }: RootState) =>
        list.items.find((conf) => conf.sessionId === sessionId);

export const selectIsArtifactsLoading = ({ conferencesHistory: { sidebar } }: RootState) =>
    sidebar.loadingArtifactsStatus === 'IN_PROGRESS';

export const selectSelectedSession = (state: RootState) => {
    const selectedSessionId = selectSelectedSessionId(state);
    return selectedSessionId ? selectSession(selectedSessionId)(state) : null;
};

export const selectSelectedConfArtifacts = ({ conferencesHistory: { sidebar } }: RootState) => sidebar.artifacts;

export const selectForceReloadOnStartup = ({ conferencesHistory: { list } }: RootState) => list.forceReloadOnStartup;
